import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import RichTextEditor from '../components/RichTextEditor';
import './StaffCommunityResourcesPage.css';

const formatResourceDateValue = (value) => {
  if (!value) return '';
  return String(value).split('T')[0];
};

const normalizeResourcePayload = (resource = {}) => ({
  title: resource.title ?? '',
  category: resource.category ?? '',
  description: resource.description ?? '',
  content: resource.content ?? '',
  thumbnailUrl: resource.thumbnailUrl ?? '',
  videoUrl: resource.videoUrl ?? '',
  externalLink: resource.externalLink ?? '',
  internalLink: {
    linkType: resource.internalLink?.linkType ?? 'none',
    linkId: resource.internalLink?.linkId ?? '',
    linkModel: resource.internalLink?.linkModel ?? '',
    linkText: resource.internalLink?.linkText ?? ''
  },
  contactInfo: {
    name: resource.contactInfo?.name ?? '',
    email: resource.contactInfo?.email ?? '',
    phone: resource.contactInfo?.phone ?? '',
    address: resource.contactInfo?.address ?? ''
  },
  tags: Array.isArray(resource.tags) ? resource.tags.filter(Boolean) : [],
  attachments: Array.isArray(resource.attachments) ? resource.attachments : [],
  isPublished: Boolean(resource.isPublished),
  isFeatured: Boolean(resource.isFeatured),
  order: Number.isFinite(resource.order) ? resource.order : 0,
  effectiveDate: formatResourceDateValue(resource.effectiveDate),
  expiryDate: formatResourceDateValue(resource.expiryDate),
  visibleTo: Array.isArray(resource.visibleTo) && resource.visibleTo.length > 0
    ? resource.visibleTo
    : ['public', 'user', 'citizen']
});

const StaffCommunityResourcesPage = () => {
  const [resources, setResources] = useState([]);
  const [filteredResources, setFilteredResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showModal, setShowModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [modalShake, setModalShake] = useState(false);

  const handleBackdropClick = () => {
    setModalShake(true);
    setTimeout(() => setModalShake(false), 500);
  };

  const [editingResource, setEditingResource] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [disclaimer, setDisclaimer] = useState('');
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [grants, setGrants] = useState([]);
  const [healthPrograms, setHealthPrograms] = useState([]);
  const [showJsonImport, setShowJsonImport] = useState(false);
  const [jsonInput, setJsonInput] = useState('');
  const [jsonError, setJsonError] = useState('');
  const JSON_TEMPLATE = JSON.stringify({
    title: 'Youth Wellness Resource Guide',
    category: 'Wellness',
    description: 'A short overview shown on the resource card.',
    content: '<p>Detailed resource information goes here.</p>',
    thumbnailUrl: 'https://example.com/resource-image.jpg',
    videoUrl: '',
    externalLink: 'https://example.com/learn-more',
    internalLink: {
      linkType: 'education',
      linkId: '',
      linkModel: 'HealthProgram',
      linkText: 'View Related Program'
    },
    contactInfo: {
      name: 'Community Support Team',
      email: 'support@rsmin.ca',
      phone: '(800) 555-0100',
      address: 'RSMIN Community Centre'
    },
    tags: ['youth', 'wellness', 'support'],
    attachments: [],
    isPublished: false,
    isFeatured: false,
    order: 0,
    effectiveDate: '2026-04-01',
    expiryDate: '',
    visibleTo: ['public', 'user', 'citizen']
  }, null, 2);
  
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: '',
    content: '',
    thumbnailUrl: '',
    videoUrl: '',
    externalLink: '',
    internalLink: {
      linkType: 'none',
      linkId: '',
      linkModel: '',
      linkText: ''
    },
    contactInfo: {
      name: '',
      email: '',
      phone: '',
      address: ''
    },
    tags: [],
    attachments: [],
    isPublished: false,
    isFeatured: false,
    order: 0,
    effectiveDate: '',
    expiryDate: '',
    visibleTo: ['public', 'user', 'citizen']
  });

  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    icon: '📁',
    description: '',
    order: 0
  });

  useEffect(() => {
    fetchResources();
    fetchCategories();
    fetchSettings();
    fetchGrants();
    fetchHealthPrograms();
  }, []);

  useEffect(() => {
    if (selectedCategory === 'All') {
      setFilteredResources(resources);
    } else {
      setFilteredResources(resources.filter(r => r.category === selectedCategory));
    }
  }, [selectedCategory, resources]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/api/categories/all');
      console.log('Fetched categories:', response.data);
      setCategories(response.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await api.get('/api/community-resources/settings');
      if (response.data.success) {
        setDisclaimer(response.data.settings.disclaimer || '');
        setShowDisclaimer(response.data.settings.showDisclaimer || false);
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  };

  const fetchGrants = async () => {
    try {
      const response = await api.get('/api/health-programs?category=education');
      setGrants(response.data.programs || []);
    } catch (err) {
      console.error('Error fetching education programs:', err);
    }
  };

  const fetchHealthPrograms = async () => {
    try {
      const response = await api.get('/api/health-programs');
      setHealthPrograms(response.data.programs || []);
    } catch (err) {
      console.error('Error fetching health programs:', err);
    }
  };

  const handleSaveDisclaimer = async () => {
    try {
      setLoading(true);
      await api.put('/api/community-resources/settings', {
        disclaimer,
        showDisclaimer
      });
      setSuccessMessage('Disclaimer updated successfully!');
      setShowDisclaimerModal(false);
      setLoading(false);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving disclaimer:', err);
      setError(err.response?.data?.error || 'Error saving disclaimer');
      setLoading(false);
    }
  };

  const fetchResources = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/community-resources');
      setResources(response.data.resources);
      setFilteredResources(response.data.resources);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching resources:', err);
      setError(err.response?.data?.error || 'Error loading resources');
      setLoading(false);
    }
  };

  const handleOpenModal = (resource = null) => {
    if (resource) {
      setEditingResource(resource);
      setFormData(normalizeResourcePayload(resource));
    } else {
      setEditingResource(null);
      // Get first active category or fallback
      const activeCategories = categories.filter(cat => cat.isActive);
      const defaultCategory = activeCategories.length > 0 ? activeCategories[0].name : '';
      
      setFormData(normalizeResourcePayload({ category: defaultCategory }));
    }
    setShowModal(true);
    setShowJsonImport(false);
    setJsonInput('');
    setJsonError('');
    setError('');
    setSuccessMessage('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingResource(null);
    setShowJsonImport(false);
    setJsonInput('');
    setJsonError('');
    setError('');
  };

  const handleJsonImport = () => {
    setJsonError('');

    try {
      const parsed = JSON.parse(jsonInput);
      setFormData((prev) => normalizeResourcePayload({
        ...prev,
        ...parsed,
        internalLink: {
          ...(prev.internalLink || {}),
          ...(parsed.internalLink || {})
        },
        contactInfo: {
          ...(prev.contactInfo || {}),
          ...(parsed.contactInfo || {})
        },
        tags: parsed.tags ?? prev.tags,
        attachments: parsed.attachments ?? prev.attachments,
        visibleTo: parsed.visibleTo ?? prev.visibleTo
      }));
      setShowJsonImport(false);
      setJsonInput('');
    } catch (err) {
      setJsonError('Invalid JSON — please check the format and try again.');
    }
  };

  const handleVisibleToChange = (tier) => {
    setFormData(prev => ({
      ...prev,
      visibleTo: prev.visibleTo.includes(tier)
        ? prev.visibleTo.filter(t => t !== tier)
        : [...prev.visibleTo, tier]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');

      // Ensure external link has a protocol
      const normalizedExternalLink = formData.externalLink && formData.externalLink.trim()
        ? formData.externalLink.trim().match(/^https?:\/\//i)
          ? formData.externalLink.trim()
          : `https://${formData.externalLink.trim()}`
        : '';

      const payload = { ...formData, externalLink: normalizedExternalLink };

      if (editingResource) {
        await api.put(`/api/community-resources/${editingResource._id}`, payload);
        setSuccessMessage('Resource updated successfully!');
      } else {
        await api.post('/api/community-resources', payload);
        setSuccessMessage('Resource created successfully!');
      }

      await fetchResources();
      handleCloseModal();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving resource:', err);
      setError(err.response?.data?.error || 'Error saving resource');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) {
      return;
    }

    try {
      await api.delete(`/api/community-resources/${id}`);
      setSuccessMessage('Resource deleted successfully!');
      await fetchResources();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting resource:', err);
      setError(err.response?.data?.error || 'Error deleting resource');
    }
  };

  const handleTogglePublish = async (id) => {
    try {
      await api.patch(`/api/community-resources/${id}/publish`);
      await fetchResources();
    } catch (err) {
      console.error('Error toggling publish:', err);
      setError(err.response?.data?.error || 'Error updating resource');
    }
  };

  const handleToggleFeatured = async (id) => {
    try {
      await api.patch(`/api/community-resources/${id}/featured`);
      await fetchResources();
    } catch (err) {
      console.error('Error toggling featured:', err);
      setError(err.response?.data?.error || 'Error updating resource');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only PDF and Word documents are allowed');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    try {
      setUploadingFile(true);
      setError('');

      const formDataFile = new FormData();
      formDataFile.append('file', file);

      const response = await api.post('/api/files/upload', formDataFile, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        const newAttachment = {
          filename: response.data.filename,
          url: response.data.url,
          fileType: response.data.fileType,
          size: response.data.size
        };

        setFormData({
          ...formData,
          attachments: [...formData.attachments, newAttachment]
        });

        setSuccessMessage('File uploaded successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err.response?.data?.error || 'Error uploading file');
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  const handleRemoveAttachment = (index) => {
    setFormData({
      ...formData,
      attachments: formData.attachments.filter((_, i) => i !== index)
    });
  };

  // Category Management Functions
  const handleOpenCategoryModal = (category = null) => {
    if (category) {
      // Editing existing category
      setEditingCategory(category);
      setCategoryFormData({
        name: category.name,
        icon: category.icon,
        description: category.description || '',
        order: category.order || 0
      });
    } else {
      // Just opening the modal to view list
      setEditingCategory(null);
    }
    setShowCategoryModal(true);
    setError('');
  };

  const handleStartCreateCategory = () => {
    // Creating new category
    setEditingCategory({});
    setCategoryFormData({
      name: '',
      icon: '📁',
      description: '',
      order: categories.length
    });
  };

  const handleCloseCategoryModal = () => {
    setShowCategoryModal(false);
    setEditingCategory(null);
    setCategoryFormData({
      name: '',
      icon: '📁',
      description: '',
      order: 0
    });
  };

  const handleSaveCategory = async () => {
    try {
      if (editingCategory?._id) {
        // Update existing category
        await api.put(`/api/categories/${editingCategory._id}`, categoryFormData);
        setSuccessMessage('Category updated successfully!');
      } else {
        // Create new category
        await api.post('/api/categories', categoryFormData);
        setSuccessMessage('Category created successfully!');
      }
      
      fetchCategories();
      handleCloseCategoryModal();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving category:', err);
      setError(err.response?.data?.message || 'Error saving category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category? This cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/api/categories/${categoryId}`);
      setSuccessMessage('Category deleted successfully!');
      fetchCategories();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting category:', err);
      setError(err.response?.data?.message || 'Error deleting category');
    }
  };

  const handleToggleCategoryActive = async (category) => {
    try {
      await api.put(`/api/categories/${category._id}`, {
        ...category,
        isActive: !category.isActive
      });
      fetchCategories();
      setSuccessMessage(`Category ${category.isActive ? 'deactivated' : 'activated'} successfully!`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error toggling category:', err);
      setError('Error updating category status');
    }
  };

  return (
    <div className="staff-community-resources-container">
      <div className="page-header">
        <h1>Manage Community Resources</h1>
        <div className="header-buttons">
          <button onClick={() => handleOpenModal()} className="btn-primary">
            + Add New Resource
          </button>
          <button onClick={() => handleOpenCategoryModal()} className="btn-secondary">
            📁 Manage Categories
          </button>
          <button onClick={() => setShowDisclaimerModal(true)} className="btn-secondary">
            ⚠️ Page Disclaimer
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      <div className="category-filters">
        <button
          className={`category-btn ${selectedCategory === 'All' ? 'active' : ''}`}
          onClick={() => setSelectedCategory('All')}
        >
          All ({resources.length})
        </button>
        {categories.filter(cat => cat.isActive).map(category => {
          const count = resources.filter(r => r.category === category.name).length;
          return (
            <button
              key={category._id}
              className={`category-btn ${selectedCategory === category.name ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category.name)}
            >
              {category.icon} {category.name} ({count})
            </button>
          );
        })}
      </div>

      {loading && !showModal ? (
        <div className="loading">Loading resources...</div>
      ) : (
        <div className="resources-grid">
          {filteredResources.map(resource => (
            <div key={resource._id} className="resource-card">
              <div className="resource-header">
                <div>
                  <h3>{resource.title}</h3>
                  <span className="category-badge">{resource.category}</span>
                </div>
                <div className="resource-status">
                  {resource.isFeatured && <span className="badge featured">⭐ Featured</span>}
                  {resource.isPublished ? (
                    <span className="badge published">✓ Published</span>
                  ) : (
                    <span className="badge draft">Draft</span>
                  )}
                </div>
              </div>

              <p className="resource-description">{resource.description}</p>

              {resource.tags && resource.tags.length > 0 && (
                <div className="resource-tags">
                  {resource.tags.map((tag, idx) => (
                    <span key={idx} className="tag">#{tag}</span>
                  ))}
                </div>
              )}

              <div className="resource-actions">
                <button onClick={() => handleOpenModal(resource)} className="btn-edit">
                  ✏️ Edit
                </button>
                <button 
                  onClick={() => handleTogglePublish(resource._id)}
                  className="btn-toggle"
                >
                  {resource.isPublished ? '👁️ Unpublish' : '📢 Publish'}
                </button>
                <button 
                  onClick={() => handleToggleFeatured(resource._id)}
                  className="btn-toggle"
                >
                  {resource.isFeatured ? '⭐ Unfeature' : '⭐ Feature'}
                </button>
                <button onClick={() => handleDelete(resource._id)} className="btn-delete">
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}

          {filteredResources.length === 0 && (
            <div className="no-resources">
              <p>{selectedCategory === 'All' ? 'No community resources yet. Create your first one!' : `No resources in ${selectedCategory} category.`}</p>
            </div>
          )}
        </div>
      )}

      {/* Modal for creating/editing resource */}
      {showModal && (
        <div className="modal-overlay" onClick={handleBackdropClick}>
          <div className={`modal-content${modalShake ? ' modal-shake' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingResource ? 'Edit Resource' : 'Create New Resource'}</h2>
              <button onClick={handleCloseModal} className="close-btn">&times;</button>
            </div>

            <div className="modal-body">
            <div className="json-import-bar resource-json-import-bar">
              <button
                type="button"
                className="btn-json-toggle"
                onClick={() => {
                  setShowJsonImport((value) => !value);
                  setJsonError('');
                }}
              >
                {showJsonImport ? '✕ Close JSON Import' : '{ } Import from JSON'}
              </button>
              {!showJsonImport && (
                <span className="json-bar-hint">Copy the current resource JSON, make edits, and reload it back into this form.</span>
              )}
            </div>

            {showJsonImport && (
              <div className="json-import-panel resource-json-import-panel">
                <div className="json-import-header">
                  <h3>📋 Edit Resource with JSON</h3>
                  <p>Use the template or copy the current resource JSON, update it, then paste it back and click <strong>Load into Form</strong>.</p>
                </div>
                <div className="json-template-block">
                  <div className="json-template-label">
                    <span>Example Template</span>
                    <div className="json-template-actions-inline">
                      <button
                        type="button"
                        className="btn-copy-json"
                        onClick={() => navigator.clipboard.writeText(JSON_TEMPLATE)}
                      >
                        📋 Copy Template
                      </button>
                      <button
                        type="button"
                        className="btn-copy-json"
                        onClick={() => navigator.clipboard.writeText(JSON.stringify(normalizeResourcePayload(formData), null, 2))}
                      >
                        📋 Copy Current JSON
                      </button>
                    </div>
                  </div>
                  <pre className="json-template-pre">{JSON_TEMPLATE}</pre>
                </div>
                <div className="json-template-block">
                  <div className="json-template-label">
                    <span>Current Resource JSON</span>
                    <div className="json-template-actions-inline">
                      <button
                        type="button"
                        className="btn-copy-json"
                        onClick={() => setJsonInput(JSON.stringify(normalizeResourcePayload(formData), null, 2))}
                      >
                        ✏ Load Current into Editor
                      </button>
                      <button
                        type="button"
                        className="btn-copy-json"
                        onClick={() => navigator.clipboard.writeText(JSON.stringify(normalizeResourcePayload(formData), null, 2))}
                      >
                        📋 Copy Current JSON
                      </button>
                    </div>
                  </div>
                  <pre className="json-template-pre">{JSON.stringify(normalizeResourcePayload(formData), null, 2)}</pre>
                </div>
                <div className="json-paste-block">
                  <label htmlFor="resource-json-paste">Paste your updated JSON here:</label>
                  <textarea
                    id="resource-json-paste"
                    className="json-paste-area"
                    rows={14}
                    value={jsonInput}
                    onChange={(e) => {
                      setJsonInput(e.target.value);
                      setJsonError('');
                    }}
                    placeholder="Paste your JSON here..."
                    spellCheck={false}
                  />
                </div>
                {jsonError && <div className="json-error">{jsonError}</div>}
                <div className="json-import-actions">
                  <button type="button" onClick={handleJsonImport} className="btn-primary" disabled={!jsonInput.trim()}>
                    ⬇ Load into Form
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowJsonImport(false);
                      setJsonInput('');
                      setJsonError('');
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="resource-form">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Category *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                >
                  {categories.filter(cat => cat.isActive).length === 0 ? (
                    <option value="">No categories available - Please create one first</option>
                  ) : (
                    categories.filter(cat => cat.isActive).map(category => (
                      <option key={category._id} value={category.name}>
                        {category.icon} {category.name}
                      </option>
                    ))
                  )}
                </select>
                {categories.filter(cat => cat.isActive).length === 0 && (
                  <small style={{color: '#dc3545'}}>
                    ⚠️ Please create at least one category before adding resources.
                  </small>
                )}
              </div>

              <div className="form-group">
                <label>Short Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief summary (shown in preview)"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Content *</label>
                <RichTextEditor
                  value={formData.content}
                  onChange={(content) => setFormData({ ...formData, content })}
                  placeholder="Enter detailed information..."
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Thumbnail URL</label>
                  <input
                    type="text"
                    value={formData.thumbnailUrl}
                    onChange={(e) => setFormData({ ...formData, thumbnailUrl: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>

                <div className="form-group">
                  <label>Video URL</label>
                  <input
                    type="text"
                    value={formData.videoUrl}
                    onChange={(e) => setFormData({ ...formData, videoUrl: e.target.value })}
                    placeholder="https://youtube.com/watch?v=..."
                  />
                </div>
              </div>

              <div className="form-group">
                <label>External Link</label>
                <input
                  type="text"
                  value={formData.externalLink}
                  onChange={(e) => setFormData({ ...formData, externalLink: e.target.value })}
                  placeholder="https://example.com"
                />
              </div>

              <div className="internal-link-section">
                <h4>🔗 Internal Link to Grant/Program</h4>
                <p className="section-description">Link this resource to an education grant or health program</p>
                
                <div className="form-group">
                  <label>Link Type</label>
                  <select
                    value={formData.internalLink.linkType}
                    onChange={(e) => {
                      const linkType = e.target.value;
                      setFormData({ 
                        ...formData, 
                        internalLink: { 
                          linkType,
                          linkId: '',
                          linkModel: linkType === 'education' ? 'HealthProgram' : linkType === 'health' ? 'HealthProgram' : '',
                          linkText: ''
                        }
                      });
                    }}
                  >
                    <option value="none">None</option>
                    <option value="education">Education Program</option>
                    <option value="health">Health Program</option>
                    <option value="housing">Housing (Coming Soon)</option>
                    <option value="employment">Employment (Coming Soon)</option>
                  </select>
                </div>

                {formData.internalLink.linkType === 'education' && (
                  <div className="form-group">
                    <label>Select Education Program</label>
                    <select
                      value={formData.internalLink.linkId}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        internalLink: { ...formData.internalLink, linkId: e.target.value }
                      })}
                    >
                      <option value="">-- Select an Education Program --</option>
                      {grants.map(grant => (
                        <option key={grant._id} value={grant._id}>
                          {grant.title} - {grant.programType || 'Program'}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {formData.internalLink.linkType === 'health' && (
                  <div className="form-group">
                    <label>Select Health Program</label>
                    <select
                      value={formData.internalLink.linkId}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        internalLink: { ...formData.internalLink, linkId: e.target.value }
                      })}
                    >
                      <option value="">-- Select a Health Program --</option>
                      {healthPrograms.map(program => (
                        <option key={program._id} value={program._id}>
                          {program.title} - {program.programType}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {formData.internalLink.linkType !== 'none' && formData.internalLink.linkType !== 'housing' && formData.internalLink.linkType !== 'employment' && (
                  <div className="form-group">
                    <label>Link Button Text</label>
                    <input
                      type="text"
                      value={formData.internalLink.linkText}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        internalLink: { ...formData.internalLink, linkText: e.target.value }
                      })}
                      placeholder="e.g., 'Apply for This Program', 'View Program Details'"
                    />
                  </div>
                )}
              </div>

              <div className="contact-info-section">
                <h4>Contact Information</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Contact Name</label>
                    <input
                      type="text"
                      value={formData.contactInfo.name}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        contactInfo: { ...formData.contactInfo, name: e.target.value }
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={formData.contactInfo.email}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        contactInfo: { ...formData.contactInfo, email: e.target.value }
                      })}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={formData.contactInfo.phone}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        contactInfo: { ...formData.contactInfo, phone: e.target.value }
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Address</label>
                    <input
                      type="text"
                      value={formData.contactInfo.address}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        contactInfo: { ...formData.contactInfo, address: e.target.value }
                      })}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Tags (comma separated)</label>
                <input
                  type="text"
                  value={formData.tags.join(', ')}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    tags: e.target.value.split(',').map(t => t.trim()).filter(t => t) 
                  })}
                  placeholder="events, wellness, youth"
                />
              </div>

              <div className="form-group">
                <label>📎 Attachments</label>
                <input
                  type="file"
                  onChange={handleFileUpload}
                  accept=".pdf,.doc,.docx"
                  disabled={uploadingFile}
                />
                {uploadingFile && <small>Uploading...</small>}
                
                {formData.attachments.length > 0 && (
                  <div className="attachments-list">
                    {formData.attachments.map((att, idx) => (
                      <div key={idx} className="attachment-item">
                        <span>📄 {att.filename}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveAttachment(idx)}
                          className="remove-attachment-btn"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Effective Date</label>
                  <input
                    type="date"
                    value={formData.effectiveDate}
                    onChange={(e) => setFormData({ ...formData, effectiveDate: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Expiry Date</label>
                  <input
                    type="date"
                    value={formData.expiryDate}
                    onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                  />
                  <small>Lower numbers appear first</small>
                </div>
              </div>

              <div className="form-group checkboxes">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isPublished}
                    onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
                  />
                  Publish Resource
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isFeatured}
                    onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })}
                  />
                  Feature Resource
                </label>
              </div>

              <div className="form-group">
                <label>Visible To (Access Levels)</label>
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.visibleTo.includes('public')}
                      onChange={() => handleVisibleToChange('public')}
                    />
                    🌍 Public (Not logged in visitors)
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.visibleTo.includes('user')}
                      onChange={() => handleVisibleToChange('user')}
                    />
                    👤 User (Logged in, not verified)
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.visibleTo.includes('citizen')}
                      onChange={() => handleVisibleToChange('citizen')}
                    />
                    ✓ Citizen (Verified with card number)
                  </label>
                </div>
                <small style={{ color: '#666', marginTop: '0.5rem', display: 'block' }}>
                  Select which user tiers can view this resource. Leave all checked for public access.
                </small>
              </div>

              <div className="form-actions">
                <button type="button" onClick={handleCloseModal} className="btn-cancel">
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="btn-submit">
                  {loading ? 'Saving...' : editingResource ? 'Update Resource' : 'Create Resource'}
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* Category Management Modal */}
      {showCategoryModal && (
        <div className="modal-overlay" onClick={handleBackdropClick}>
          <div className={`modal-content${modalShake ? ' modal-shake' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingCategory?._id ? 'Edit Category' : editingCategory ? 'Create Category' : 'Manage Categories'}</h2>
              <button onClick={handleCloseCategoryModal} className="close-btn">&times;</button>
            </div>
            
            {!editingCategory ? (
              <div className="categories-list">
                <h3>Existing Categories</h3>
                <div className="category-items">
                  {categories.map((category) => (
                    <div key={category._id} className="category-item">
                      <div className="category-info">
                        <span className="category-icon">{category.icon}</span>
                        <div>
                          <strong>{category.name}</strong>
                          {category.description && <p className="category-desc">{category.description}</p>}
                        </div>
                        <span className={`status-badge ${category.isActive ? 'active' : 'inactive'}`}>
                          {category.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="category-actions">
                        <button onClick={() => handleOpenCategoryModal(category)} className="btn-edit">
                          ✏️ Edit
                        </button>
                        <button 
                          onClick={() => handleToggleCategoryActive(category)} 
                          className="btn-toggle"
                        >
                          {category.isActive ? '🔴 Deactivate' : '🟢 Activate'}
                        </button>
                        <button onClick={() => handleDeleteCategory(category._id)} className="btn-delete">
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <button onClick={handleStartCreateCategory} className="btn-add-category">
                  + Add New Category
                </button>
              </div>
            ) : (
              <form onSubmit={(e) => { e.preventDefault(); handleSaveCategory(); }} className="category-form">
                <div className="form-group">
                  <label>Category Name *</label>
                  <input
                    type="text"
                    value={categoryFormData.name}
                    onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                    required
                    placeholder="e.g., Community Events"
                  />
                </div>

                <div className="form-group">
                  <label>Icon *</label>
                  <select
                    value={categoryFormData.icon}
                    onChange={(e) => setCategoryFormData({ ...categoryFormData, icon: e.target.value })}
                    required
                    className="icon-select"
                  >
                    <option value="🎉">🎉 Party Popper (Events)</option>
                    <option value="🎊">🎊 Confetti Ball (Celebrations)</option>
                    <option value="🎭">🎭 Performing Arts (Culture)</option>
                    <option value="🎨">🎨 Art (Creative Programs)</option>
                    <option value="🎵">🎵 Music (Music Programs)</option>
                    <option value="💚">💚 Green Heart (Health)</option>
                    <option value="❤️">❤️ Red Heart (Wellness)</option>
                    <option value="🏥">🏥 Hospital (Medical)</option>
                    <option value="🧘">🧘 Meditation (Mental Health)</option>
                    <option value="🤝">🤝 Handshake (Support)</option>
                    <option value="👥">👥 People (Community)</option>
                    <option value="👴">👴 Elder (Elder Services)</option>
                    <option value="👵">👵 Elder Woman (Senior Programs)</option>
                    <option value="👦">👦 Boy (Youth Programs)</option>
                    <option value="👧">👧 Girl (Youth Services)</option>
                    <option value="👶">👶 Baby (Childcare)</option>
                    <option value="⚽">⚽ Soccer (Sports)</option>
                    <option value="🏀">🏀 Basketball (Athletics)</option>
                    <option value="🎯">🎯 Target (Recreation)</option>
                    <option value="🏊">🏊 Swimming (Aquatics)</option>
                    <option value="🏠">🏠 House (Housing)</option>
                    <option value="🏘️">🏘️ Houses (Community Housing)</option>
                    <option value="💼">💼 Briefcase (Employment)</option>
                    <option value="💻">💻 Laptop (Technology)</option>
                    <option value="📚">📚 Books (Education)</option>
                    <option value="🎓">🎓 Graduation (Learning)</option>
                    <option value="🍎">🍎 Apple (Nutrition)</option>
                    <option value="🌱">🌱 Seedling (Environment)</option>
                    <option value="🌿">🌿 Herb (Nature Programs)</option>
                    <option value="🦅">🦅 Eagle (Traditional)</option>
                    <option value="🪶">🪶 Feather (Indigenous)</option>
                    <option value="🔔">🔔 Bell (Announcements)</option>
                    <option value="📢">📢 Megaphone (News)</option>
                    <option value="📍">📍 Pin (Location/Other)</option>
                    <option value="✨">✨ Sparkles (Special)</option>
                    <option value="📁">📁 Folder (General)</option>
                  </select>
                  <small>Select an icon for your category</small>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={categoryFormData.description}
                    onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                    placeholder="Optional description"
                    rows={3}
                  />
                </div>

                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    value={categoryFormData.order}
                    onChange={(e) => setCategoryFormData({ ...categoryFormData, order: parseInt(e.target.value) })}
                    min={0}
                  />
                </div>

                <div className="form-actions">
                  <button type="button" onClick={handleCloseCategoryModal} className="btn-cancel">
                    Cancel
                  </button>
                  <button type="submit" className="btn-submit">
                    {editingCategory?._id ? 'Update Category' : 'Create Category'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Disclaimer Modal */}
      {showDisclaimerModal && (
        <div className="modal-overlay">
          <div className="modal-content category-modal">
            <div className="modal-header">
              <h2>⚠️ Manage Page Disclaimer</h2>
              <button onClick={() => setShowDisclaimerModal(false)} className="btn-close">×</button>
            </div>

            <div className="disclaimer-info">
              <p>This disclaimer will appear at the top of the public Community Resources page. Use it to provide important notices or information to community members.</p>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={showDisclaimer}
                  onChange={(e) => setShowDisclaimer(e.target.checked)}
                />
                {' '}Show disclaimer on public page
              </label>
            </div>

            <div className="form-group">
              <label>Disclaimer Text</label>
              <textarea
                value={disclaimer}
                onChange={(e) => setDisclaimer(e.target.value)}
                placeholder="Enter disclaimer text that will appear on the Community Resources page..."
                rows={6}
                style={{ width: '100%', padding: '10px', fontSize: '1rem', lineHeight: '1.5' }}
              />
              <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                This text will be displayed in a warning box at the top of the page.
              </small>
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                onClick={() => setShowDisclaimerModal(false)} 
                className="btn-cancel"
              >
                Cancel
              </button>
              <button 
                onClick={handleSaveDisclaimer} 
                className="btn-submit"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Disclaimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffCommunityResourcesPage;
